import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { deleteDialog } from 'src/app/shared/dialog';
import { AGENT_PERMISSIONS, LlmModel, ModelRequest, Provider, REASONING_EFFORT_LABELS, REASONING_EFFORT_VALUES } from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';
import { blankToNull, numOrNull } from '../../utils/clipboard';

interface ModelForm {
  providerId: string;
  modelId: string;
  displayName: string;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  supportsTemperature: boolean;
  /** The model accepts images (needed for agents with image input). */
  supportsVision: boolean;
  /** Contract v1.4: the model can call tools (playbooks). Default on. */
  supportsTools: boolean;
  inputPricePer1M: number | null;
  outputPricePer1M: number | null;
  enabled: boolean;
  /** Thinking levels the provider accepts for this model (empty = none). */
  reasoningEfforts: string[];
  defaultReasoningEffort: string;
}

/** Contract v1.4: an older service leaves supportsTools out, which means true. */
export function modelSupportsTools(m: Pick<LlmModel, 'supportsTools'>): boolean {
  return m.supportsTools !== false;
}

/** The thinking levels of a model for the table: "low, medium, high (default medium)" or "-". */
export function reasoningSummary(m: Pick<LlmModel, 'reasoningEfforts' | 'defaultReasoningEffort'>): string {
  const list = m.reasoningEfforts || [];
  if (!list.length) return '-';
  return list.join(', ') + (m.defaultReasoningEffort ? ` (default ${m.defaultReasoningEffort})` : '');
}

@Component({
  selector: 'app-ai-models-page',
  templateUrl: './models-page.component.html',
  styleUrls: ['../../ai-agents.shared.css'],
})
export class ModelsPageComponent implements OnInit {
  readonly P = AGENT_PERMISSIONS;
  readonly effortValues = REASONING_EFFORT_VALUES;
  readonly effortLabels = REASONING_EFFORT_LABELS;
  readonly reasoningSummary = reasoningSummary;
  readonly supportsTools = modelSupportsTools;

  providers: Provider[] = [];
  models: LlmModel[] = [];
  providerFilter = '';
  loading = false;

  editorOpen = false;
  editingId: string | null = null;
  form: ModelForm = this.blankForm();
  formError = '';

  constructor(
    private api: AiAgentsApiService,
    public perms: AgentPermissionsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.providerFilter = this.route.snapshot.queryParamMap.get('providerId') || '';
    if (this.perms.has(this.P.providerList)) {
      this.api.listProviders().subscribe({ next: (list) => (this.providers = list || []) });
    }
    this.load();
  }

  providerName(id: string): string {
    const p = this.providers.find((x) => x.id === id);
    return p ? p.displayName : id;
  }

  onFilterChange(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { providerId: this.providerFilter || null },
      replaceUrl: true,
    });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.api.listModels(this.providerFilter || null).subscribe({
      next: (list) => {
        this.models = list || [];
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  openCreate(): void {
    this.editingId = null;
    this.form = this.blankForm();
    this.form.providerId = this.providerFilter || this.providers[0]?.id || '';
    this.formError = '';
    this.editorOpen = true;
  }

  openEdit(m: LlmModel): void {
    this.editingId = m.id;
    this.form = {
      providerId: m.providerId,
      modelId: m.modelId,
      displayName: m.displayName,
      contextWindow: m.contextWindow,
      maxOutputTokens: m.maxOutputTokens,
      supportsTemperature: m.supportsTemperature,
      supportsVision: m.supportsVision === true,
      supportsTools: m.supportsTools !== false,
      inputPricePer1M: m.inputPricePer1M,
      outputPricePer1M: m.outputPricePer1M,
      enabled: m.enabled,
      reasoningEfforts: [...(m.reasoningEfforts || [])],
      defaultReasoningEffort: m.defaultReasoningEffort || '',
    };
    this.formError = '';
    this.editorOpen = true;
  }

  hasEffort(value: string): boolean {
    return this.form.reasoningEfforts.includes(value);
  }

  /** Keeps the list in the service order; a default that leaves the list is cleared. */
  toggleEffort(value: string, on: boolean): void {
    const set = new Set(this.form.reasoningEfforts);
    if (on) set.add(value);
    else set.delete(value);
    this.form.reasoningEfforts = this.effortValues.filter((v) => set.has(v));
    if (this.form.defaultReasoningEffort && !set.has(this.form.defaultReasoningEffort)) {
      this.form.defaultReasoningEffort = '';
    }
  }

  closeEditor(): void {
    this.editorOpen = false;
  }

  save(): void {
    const body: ModelRequest = {
      providerId: blankToNull(this.form.providerId),
      modelId: blankToNull(this.form.modelId),
      displayName: blankToNull(this.form.displayName),
      contextWindow: numOrNull(this.form.contextWindow),
      maxOutputTokens: numOrNull(this.form.maxOutputTokens),
      supportsTemperature: this.form.supportsTemperature,
      supportsVision: this.form.supportsVision,
      supportsTools: this.form.supportsTools,
      inputPricePer1M: numOrNull(this.form.inputPricePer1M),
      outputPricePer1M: numOrNull(this.form.outputPricePer1M),
      enabled: this.form.enabled,
      reasoningEfforts: this.form.reasoningEfforts,
      defaultReasoningEffort: this.form.defaultReasoningEffort || '',
    };
    if (!body.providerId || !body.modelId || !body.displayName) {
      this.formError = 'Provider, model ID and display name are required.';
      return;
    }
    const call = this.editingId ? this.api.updateModel(this.editingId, body) : this.api.createModel(body);
    call.subscribe({
      next: () => {
        this.editorOpen = false;
        this.load();
      },
      error: (err) => (this.formError = err?.error?.message || 'Saving failed.'),
    });
  }

  toggleEnabled(m: LlmModel): void {
    this.api.setModelEnabled(m.id, !m.enabled).subscribe({
      next: (updated) => (this.models = this.models.map((x) => (x.id === updated.id ? updated : x))),
    });
  }

  async remove(m: LlmModel): Promise<void> {
    const confirm = await deleteDialog();
    if (!confirm.isConfirmed) {
      return;
    }
    this.api.deleteModel(m.id).subscribe({ next: () => this.load() });
  }

  private blankForm(): ModelForm {
    return {
      providerId: '',
      modelId: '',
      displayName: '',
      contextWindow: null,
      maxOutputTokens: null,
      supportsTemperature: true,
      supportsVision: false,
      supportsTools: true,
      inputPricePer1M: null,
      outputPricePer1M: null,
      enabled: true,
      reasoningEfforts: [],
      defaultReasoningEffort: '',
    };
  }
}
